"use client";

import { useContext, useTransition } from "react";
import { useSession } from "next-auth/react";
import { generateUserStripe } from "@/actions/generate-user-stripe";
import { SubscriptionPlan, UserSubscriptionPlan } from "@/types/index";
import { trackInitiateCheckoutPixel } from "@/lib/facebookClient";
import { trackClientEvent } from "@/lib/analyticsClient";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Icons } from "@/components/shared/icons";
import { ModalContext } from "@/components/modals/providers";

interface BillingFormButtonProps {
  offer: SubscriptionPlan;
  subscriptionPlan: UserSubscriptionPlan;
  year: boolean;
  shouldStartTrial: boolean;
  // Overrides the year toggle; currently only the Quant card offers quarterly.
  interval?: "monthly" | "quarterly" | "yearly";
}

export function BillingFormButton({
  year,
  offer,
  subscriptionPlan,
  shouldStartTrial,
  interval,
}: BillingFormButtonProps) {
  let [isPending, startTransition] = useTransition();
  const { data: session, status } = useSession();
  const { setShowSignInModal } = useContext(ModalContext);
  
  const billingInterval = interval ?? (year ? "yearly" : "monthly");
  const priceId = offer.stripeIds[billingInterval] ?? null;
  const generateUserStripeSession = generateUserStripe.bind(null, priceId);

  const handleButtonClick = () => {
    // Check if user is authenticated before proceeding
    if (status === "unauthenticated") {
      setShowSignInModal(true);
      return;
    }
    
    // Track InitiateCheckout event (client-side + server-side)
    const planType = offer.title;
    const planValue = offer.prices[billingInterval] ?? offer.prices.monthly;
    const eventId = `initiate_checkout_${session?.user?.id ?? 'unknown'}_${Date.now()}`;
    
    // Client-side: Track with Facebook Pixel
    trackInitiateCheckoutPixel(planType, planValue, priceId || undefined, eventId);
    
    // Track to GA4 (client-side)
    trackClientEvent({
      name: 'initiate_checkout',
      params: {
        plan_type: planType,
        plan_value: planValue,
        price_id: priceId,
      },
      userId: session?.user?.id,
      userEmail: session?.user?.email || undefined,
    });
    
    // Server-side: Send to CAPI for deduplication
    if (session?.user?.email && session?.user?.id) {
      const userEmail = session.user.email;
      const userId = session.user.id;
      fetch('/api/analytics/facebook/initiate-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planType,
          planValue,
          priceId,
          eventId,
          email: userEmail,
          userId: userId,
        }),
      }).catch(() => {}); // Fire and forget
    }
    
    // If authenticated, proceed with Stripe session
    startTransition(async () => await generateUserStripeSession());
  };

  const userOffer =
    !!priceId && subscriptionPlan?.stripePriceId === priceId;

  const isPro = offer.title === "QuantWheel PRO";
  const isQuant = offer.title === "QuantWheel Quant";
  const highlight = isPro || isQuant;

  const trialLabel = "Start 7 Days for $1";

  return (
    <Button
      variant={highlight ? "default" : "outline"}
      size="lg"
      rounded="full"
      className={cn(
        "w-full shadow-sm hover:shadow",
        highlight &&
          "border-0 bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:opacity-90",
      )}
      disabled={isPending || status === "loading"}
      onClick={handleButtonClick}
    >
      {isPending ? (
        <>
          <Icons.spinner className="mr-2 size-4 animate-spin" /> Loading...
        </>
      ) : (
        <>{userOffer ? "Manage Subscription" : shouldStartTrial ? trialLabel : "Upgrade"}</>
      )}
    </Button>
  );
}
